/**
 * FareMeterPanel — driver-side "Estimated live fare" view shown during
 * trip_in_progress when the NAV/METER toggle is on METER.
 *
 * IMPORTANT LABELLING (regulatory):
 *   This component must NEVER be presented as a "taxi meter" or "metered
 *   fare". The user-facing label is "Estimated live fare". The final
 *   chargeable fare is computed on the backend at /driver/complete.
 *
 * Variants:
 *   - "driver" (default): full taxi-meter aesthetic — black panel with a
 *     bright neon-green LED-digit total. Designed for at-a-glance reading
 *     on a phone mounted in a vehicle.
 *   - "passenger": light/neutral surface — used inside the passenger
 *     live-fare view where the brighter meter aesthetic would feel out of
 *     place next to map / driver-info cards.
 *
 * Behaviour:
 *   - Polls /api/driver/live-fare/{rideId} every 5 seconds for the
 *     server-side authoritative breakdown.
 *   - Client-side 1-second elapsed-time ticker so the mm:ss readout
 *     feels live without spamming the backend.
 *   - For fixed-route rides, displays the agreed flat fare unchanged with
 *     a "Fixed route · flat fare" badge.
 *
 * State preservation:
 *   The parent (Driver.jsx) controls which view (NAV vs METER) is active.
 *   Switching tabs unmounts/remounts THIS component but DOES NOT touch
 *   activeRide state, so completion + WS handlers keep running.
 */
import React, { useEffect, useState, useRef } from "react";
import { api } from "../lib/api";
import { Clock, MapPin, TrendingUp, Info } from "lucide-react";

const POLL_MS = 5000;

const formatMMSS = (totalSec) => {
  const s = Math.max(0, Math.floor(totalSec || 0));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${String(m).padStart(2, "0")}:${String(r).padStart(2, "0")}`;
};

// Format the live fare as a 7-segment-style "0000.00" string padded to a
// fixed width so the digits don't jitter as the number grows.
const formatLEDAmount = (n) => {
  const v = Math.max(0, Number(n) || 0);
  return v.toFixed(2).padStart(7, "0"); // "0000.00" min width
};

// Surge is a MULTIPLIER (1.00× = standard pricing, 1.50× = +50%, 2.00× =
// double). 1.00× means NO surge — show an explicit "NONE" so it can't be
// misread as a 100% surcharge (the LED font also mangles "1.00×" badly).
const formatSurge = (m) => {
  const v = Number(m) || 0;
  return v > 1.0 ? `${v.toFixed(2)}×` : "NONE";
};

// True late-night surcharge rate (%). The surcharge is applied to the
// pre-late-night subtotal, which is `gross − late_night_amount`. Dividing
// by the VAT-net subtotal (which already includes the surcharge) understates
// the rate, so we derive it from the correct base here.
const lateNightPct = (b) => {
  const base = (b?.gross ?? 0) - (b?.late_night_amount ?? 0);
  if (b?.late_night_rate != null) return Math.round(b.late_night_rate * 100);
  return base > 0 ? Math.round(((b?.late_night_amount ?? 0) / base) * 100) : 0;
};

const Row = ({ label, value, testid, variant }) => (
  <div className="flex items-center justify-between text-xs py-1">
    <span
      className={`uppercase tracking-wider text-[10px] ${
        variant === "driver" ? "text-[#9CA3AF]" : "text-muted-foreground"
      }`}
    >
      {label}
    </span>
    <span
      className={`font-mono-tab font-bold ${
        variant === "driver" ? "text-white" : "text-foreground"
      }`}
      data-testid={testid}
    >
      {value ?? "—"}
    </span>
  </div>
);

export const FareMeterPanel = ({
  rideId,
  startedAt,
  fallbackEstimate,
  endpoint = "/driver/live-fare",
  variant = "driver",
}) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const pollRef = useRef(null);

  // Live mm:ss ticker — independent of the 5s backend poll so the seconds
  // count up smoothly. Derived from startedAt prop on the active ride.
  const [nowMs, setNowMs] = useState(() => Date.now());
  useEffect(() => {
    const tick = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(tick);
  }, []);

  const fetchFare = async () => {
    if (!rideId) return;
    try {
      const res = await api.get(`${endpoint}/${rideId}`);
      setData(res.data);
      setError(null);
    } catch (e) {
      // 404 = ride no longer in_progress (probably just completed). 409 = no
      // started_at yet. Either way the UI shows the booking estimate fallback.
      setError(e?.response?.status || "fetch_failed");
    }
  };

  useEffect(() => {
    fetchFare();
    pollRef.current = setInterval(fetchFare, POLL_MS);
    return () => clearInterval(pollRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rideId, endpoint]);

  const elapsedSec = startedAt
    ? Math.max(0, (nowMs - new Date(startedAt).getTime()) / 1000)
    : (data?.elapsed_seconds ?? 0);

  // `hasLive` becomes true after the first successful /live-fare poll and
  // stays true (data is retained on subsequent failures), so we keep
  // showing the last good live metered fare instead of dropping back to
  // the (typically higher) booking estimate. This prevents the fare from
  // appearing to "drop" from estimate → final without explanation.
  const hasLive = !!data;
  const liveFare = data?.live_fare;
  const bookingEstimate = fallbackEstimate != null ? Number(fallbackEstimate) : null;
  const breakdown = data?.breakdown;
  const isFixed = !!data?.is_fixed_route;

  // === DRIVER VARIANT — taxi-meter aesthetic ===
  // Black background, neon-green LED-digit total. The bordered digital
  // display is sized so "0000.00" fits without overflow on a 360px-wide
  // phone screen.
  if (variant === "driver") {
    return (
      <div
        className="bg-[#0A0A0A] border-2 border-[#10B981] p-4 space-y-3"
        data-testid="fare-meter-panel"
        role="status"
        aria-live="polite"
      >
        <div className="flex items-center justify-between">
          <div className="label-mini text-[#9CA3AF] tracking-[0.2em]" data-testid="fare-meter-label">
            {hasLive ? "LIVE · METERED FARE" : "BOOKING ESTIMATE"}
          </div>
          {isFixed && (
            <div
              className="text-[10px] uppercase tracking-wider font-bold bg-transparent text-[#FBBF24] border border-[#FBBF24] px-1.5 py-0.5"
              data-testid="fare-meter-fixed-badge"
            >
              Fixed · flat
            </div>
          )}
        </div>

        {/* LED display panel — black inset, segmented digits via DSEG7
            font. Glow is intentionally restrained: one inner shadow at
            1px for definition and a faint 5px halo for the LED feel.
            This keeps the digits CRISP in daylight (the previous 12px
            blur smeared them into an unreadable neon bloom). */}
        <div className="bg-black border border-[#1F2937] px-3 py-3 flex flex-col items-center justify-center overflow-hidden min-h-[72px]">
          {hasLive ? (
            <div
              className="font-led text-[#22C55E] text-[44px] sm:text-[56px] leading-none"
              style={{
                textShadow: "0 0 1px rgba(34,197,94,0.9), 0 0 5px rgba(34,197,94,0.35)",
              }}
              data-testid="fare-meter-amount"
            >
              £{formatLEDAmount(liveFare)}
            </div>
          ) : (
            <div className="flex flex-col items-center gap-1.5" data-testid="fare-meter-calculating">
              <div className="text-[#22C55E] text-sm font-bold uppercase tracking-[0.15em] animate-pulse">
                Calculating live fare…
              </div>
              {bookingEstimate != null && (
                <div className="text-[11px] text-[#9CA3AF] font-mono-tab" data-testid="fare-meter-booking-estimate">
                  Booking estimate · £{bookingEstimate.toFixed(2)}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="grid grid-cols-3 gap-2">
          <div className="border border-[#1F2937] bg-[#0F172A] p-2">
            <div className="label-mini text-[#9CA3AF] flex items-center gap-1">
              <Clock className="w-3 h-3" /> ELAPSED
            </div>
            <div className="font-led text-[#22C55E] text-base mt-0.5" data-testid="fare-meter-elapsed">
              {formatMMSS(elapsedSec)}
            </div>
          </div>
          <div className="border border-[#1F2937] bg-[#0F172A] p-2">
            <div className="label-mini text-[#9CA3AF] flex items-center gap-1">
              <MapPin className="w-3 h-3" /> DISTANCE
            </div>
            <div className="font-led text-[#22C55E] text-base mt-0.5" data-testid="fare-meter-distance">
              {data?.distance_mi != null ? `${data.distance_mi} mi` : (data?.distance_km != null ? `${data.distance_km} km` : "—")}
            </div>
          </div>
          <div className="border border-[#1F2937] bg-[#0F172A] p-2">
            <div className="label-mini text-[#9CA3AF] flex items-center gap-1">
              <TrendingUp className="w-3 h-3" /> SURGE
            </div>
            <div className="font-mono-tab font-bold text-[#22C55E] text-base mt-0.5" data-testid="fare-meter-surge">
              {formatSurge(breakdown?.surge_multiplier)}
            </div>
          </div>
        </div>

        {breakdown && (
          <div className="bg-[#0F172A] border border-[#1F2937] p-3" data-testid="fare-meter-breakdown">
            <div className="label-mini text-[#9CA3AF] mb-2">BREAKDOWN</div>
            {!isFixed && (
              <>
                <Row variant="driver" label="Base fare" value={`£${(breakdown.base_fare ?? 0).toFixed(2)}`} testid="fare-row-base" />
                <Row variant="driver" label="Distance" value={`£${(breakdown.distance_charge ?? 0).toFixed(2)}`} testid="fare-row-distance" />
                <Row variant="driver" label="Time" value={`£${(breakdown.time_charge ?? 0).toFixed(2)}`} testid="fare-row-time" />
              </>
            )}
            {isFixed && (
              <Row variant="driver" label="Flat route fare" value={`£${(breakdown.meter_total ?? 0).toFixed(2)}`} testid="fare-row-fixed" />
            )}
            {(breakdown.extras?.total ?? 0) > 0 && (
              <Row variant="driver" label="Extras" value={`£${breakdown.extras.total.toFixed(2)}`} testid="fare-row-extras" />
            )}
            {breakdown.late_night && (
              <Row variant="driver" label={`Late-night (+${lateNightPct(breakdown)}%)`} value={`£${(breakdown.late_night_amount ?? 0).toFixed(2)}`} testid="fare-row-late-night" />
            )}
            <div className="border-t border-[#1F2937] mt-2 pt-2">
              <Row variant="driver" label="Total (gross)" value={`£${(breakdown.gross ?? 0).toFixed(2)}`} testid="fare-row-gross" />
            </div>
            {(breakdown.vat_amount ?? 0) > 0 && (
              <div className="text-[10px] text-[#9CA3AF] mt-1 font-mono-tab" data-testid="fare-row-vat">
                incl. £{breakdown.vat_amount.toFixed(2)} VAT @ {Math.round((breakdown.vat_rate ?? 0) * 100)}%
              </div>
            )}
          </div>
        )}

        <div className="flex items-start gap-2 text-[11px] text-[#9CA3AF]" data-testid="fare-meter-disclaimer">
          <Info className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
          <span>
            Estimated live fare for display only. Final fare is computed and confirmed at drop-off.
          </span>
        </div>

        {error && !data && (
          <div className="text-[11px] text-[#FBBF24] bg-transparent border border-[#FBBF24] px-2 py-1" data-testid="fare-meter-stale">
            Live update unavailable — showing booking estimate.
          </div>
        )}
      </div>
    );
  }

  // === PASSENGER VARIANT — original light/neutral surface ===
  return (
    <div
      className="border-2 border-[#0A0A0A] bg-[#FAFAFB] p-4 space-y-3"
      data-testid="fare-meter-panel"
      role="status"
      aria-live="polite"
    >
      <div className="flex items-baseline justify-between gap-3">
        <div>
          <div className="label-mini text-muted-foreground" data-testid="fare-meter-label">
            {hasLive ? "LIVE METERED FARE" : "BOOKING ESTIMATE"}
          </div>
          {isFixed && (
            <div className="inline-block mt-0.5 text-[10px] uppercase tracking-wider font-bold bg-[#FFFBEB] text-[#92400E] border border-[#FFC107] px-1.5 py-0.5" data-testid="fare-meter-fixed-badge">
              Fixed route · flat fare
            </div>
          )}
        </div>
        {hasLive ? (
          <div className="font-mono-tab font-black text-3xl md:text-4xl text-foreground tabular-nums" data-testid="fare-meter-amount">
            £{(liveFare ?? 0).toFixed(2)}
          </div>
        ) : (
          <div className="flex flex-col items-end gap-0.5" data-testid="fare-meter-calculating">
            <div className="text-sm font-bold uppercase tracking-wider text-muted-foreground animate-pulse">
              Calculating live fare…
            </div>
            {bookingEstimate != null && (
              <div className="text-xs text-muted-foreground font-mono-tab" data-testid="fare-meter-booking-estimate">
                Booking estimate · £{bookingEstimate.toFixed(2)}
              </div>
            )}
          </div>
        )}
      </div>

      <div className="grid grid-cols-3 gap-2 border-t border-border pt-3">
        <div className="border border-border bg-white p-2">
          <div className="label-mini text-muted-foreground flex items-center gap-1">
            <Clock className="w-3 h-3" /> ELAPSED
          </div>
          <div className="font-mono-tab font-black text-base mt-0.5" data-testid="fare-meter-elapsed">
            {formatMMSS(elapsedSec)}
          </div>
        </div>
        <div className="border border-border bg-white p-2">
          <div className="label-mini text-muted-foreground flex items-center gap-1">
            <MapPin className="w-3 h-3" /> DISTANCE
          </div>
          <div className="font-mono-tab font-black text-base mt-0.5" data-testid="fare-meter-distance">
            {data?.distance_mi != null ? `${data.distance_mi} mi` : (data?.distance_km != null ? `${data.distance_km} km` : "—")}
          </div>
        </div>
        <div className="border border-border bg-white p-2">
          <div className="label-mini text-muted-foreground flex items-center gap-1">
            <TrendingUp className="w-3 h-3" /> SURGE
          </div>
          <div className="font-mono-tab font-black text-base mt-0.5" data-testid="fare-meter-surge">
            {formatSurge(breakdown?.surge_multiplier)}
          </div>
        </div>
      </div>

      {breakdown && (
        <div className="bg-white border border-border p-3" data-testid="fare-meter-breakdown">
          <div className="label-mini text-muted-foreground mb-2">BREAKDOWN</div>
          {!isFixed && (
            <>
              <Row label="Base fare" value={`£${(breakdown.base_fare ?? 0).toFixed(2)}`} testid="fare-row-base" />
              <Row label="Distance" value={`£${(breakdown.distance_charge ?? 0).toFixed(2)}`} testid="fare-row-distance" />
              <Row label="Time" value={`£${(breakdown.time_charge ?? 0).toFixed(2)}`} testid="fare-row-time" />
            </>
          )}
          {isFixed && (
            <Row label="Flat route fare" value={`£${(breakdown.meter_total ?? 0).toFixed(2)}`} testid="fare-row-fixed" />
          )}
          {(breakdown.extras?.total ?? 0) > 0 && (
            <Row label="Extras" value={`£${breakdown.extras.total.toFixed(2)}`} testid="fare-row-extras" />
          )}
          {breakdown.late_night && (
            <Row label={`Late-night (+${lateNightPct(breakdown)}%)`} value={`£${(breakdown.late_night_amount ?? 0).toFixed(2)}`} testid="fare-row-late-night" />
          )}
          <div className="border-t border-border mt-2 pt-2">
            <Row label="Total (gross)" value={`£${(breakdown.gross ?? 0).toFixed(2)}`} testid="fare-row-gross" />
          </div>
          {(breakdown.vat_amount ?? 0) > 0 && (
            <div className="text-[10px] text-muted-foreground mt-1 font-mono-tab" data-testid="fare-row-vat">
              incl. £{breakdown.vat_amount.toFixed(2)} VAT @ {Math.round((breakdown.vat_rate ?? 0) * 100)}%
            </div>
          )}
        </div>
      )}

      <div className="flex items-start gap-2 text-[11px] text-muted-foreground" data-testid="fare-meter-disclaimer">
        <Info className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
        <span>
          Estimated live fare for display only. Final fare is computed and confirmed at drop-off.
        </span>
      </div>

      {error && !data && (
        <div className="text-[11px] text-[#92400E] bg-[#FFFBEB] border border-[#FFC107] px-2 py-1" data-testid="fare-meter-stale">
          Live update unavailable — showing booking estimate.
        </div>
      )}
    </div>
  );
};

export default FareMeterPanel;
